/**
 * Phase 8 T-25 §29.D — Next-step bridge.
 *
 * Five-chip action row that sits ABOVE the middle-column
 * `Sourcing / Shortlisted / Hidden` sub-tabs once Stage 3 (results) is
 * reached. Answers "the results are here, now what?".
 *
 * Chip order (do NOT re-order):
 *   1. [ Draft outreach ]        — emerald outline; opens bulk compose.
 *   2. [ Create sequence ]       — outline; routes to /sequences/new.
 *   3. [ Compare ]               — outline; < 2 ticked → disabled+tooltip.
 *   4. [ Export CSV ]            — outline; downloads visible list.
 *   5. [ Explain these results ] — outline; pushes a seed prompt into the
 *                                  canvas agent thread.
 *
 * NOTE (Wrangle-parity): the old "Shortlist top N" dropdown was removed
 * because the middle column already carries a cleaner `Shortlist All (N)`
 * + `Select a collection` bulk toolbar (see `MatchCanvasLayout`). Keeping
 * both was redundant and wasted vertical space on narrower laptops.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Download,
  FileDown,
  FileSpreadsheet,
  Filter,
  GitBranch,
  Grid3x3,
  HelpCircle,
  History,
  Keyboard,
  List,
  Mail,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  SplitSquareHorizontal,
  Table2,
} from 'lucide-react';
import type { MatchResult } from '../types';
import type { SmartMatchCollectionRef } from '../lib/smartMatchThreads';
import { stashPendingRecipients } from '../lib/pendingSequenceRecipients';
import { clientNameFromMatch, jobTitleFromMatch } from '../lib/matchDisplay';

export interface NextStepBridgeProps {
  /** Current thread id — passed through to `/sequences/new?threadId=…`. */
  threadId: string;
  /** Visible matches (post-filter / post-tab) — Export CSV serialises these. */
  visibleMatches: MatchResult[];
  /** Candidates the user has ticked (usually via row selection). */
  tickedKeys: Set<string>;
  /** Currently attached collection (if any). Retained so Create sequence can
   *  scope enrolments to the shortlisted set when no rows are ticked. */
  collection: SmartMatchCollectionRef | null;
  /** Pre-fill the bulk compose panel with the current scope. */
  onDraftOutreach: () => void;
  /** §29.D chip 3 — open `<CompareDrawer>` (T-26). Pre-T-26 callers
   *  passed a toast stub here; the field name is preserved so existing
   *  wiring keeps compiling. */
  onCompareStub: () => void;
  /** Notify the parent that a toast should show (e.g. for "< 2 ticked"). */
  pushToast?: (message: string, tone?: 'ok' | 'err') => void;
  /**
   * Post user-feedback: hoist the "+ New chat" affordance into the top
   * action bar so every action (new chat, draft outreach, create sequence,
   * compare, export, explain) sits on the same row. When omitted the
   * button is hidden — Smart Match doesn't need it (its intake lives on a
   * separate page). Sourcing passes `() => navigate('/sourcing/new')`.
   */
  onNewChat?: () => void;
  /**
   * When `false`, only the leading `+ New chat` button renders. The five
   * action chips (draft outreach, create sequence, compare, export CSV,
   * explain results) are suppressed because they operate on a result set
   * that doesn't exist yet. This lets the bridge serve double duty as the
   * canvas-wide top bar (always visible) without cluttering the empty
   * state with post-results actions. Defaults to `true`.
   */
  showActionChips?: boolean;
  /**
   * Post user-feedback (image, 21-Apr): hoist the tab pill group
   * (Sourcing / Shortlisted / Hidden) into the SAME toolbar row as the
   * action chips so the canvas has ONE bar across the top instead of
   * two stacked ones. Passing an empty array or omitting the prop hides
   * the pills (Smart Match uses the default).
   */
  tabs?: Array<{ id: string; label: string; count: number }>;
  activeTabId?: string;
  onTabPick?: (id: string) => void;
  /**
   * Inline "Filter candidates" affordance. Same motivation as `tabs` — it
   * used to live in a secondary sticky strip; now it shares the toolbar.
   *
   * Post user-feedback (21-Apr): this no longer pushes a slash-command
   * into the agent composer — it toggles a real, client-side
   * `CanvasFilterPanel` popover owned by the parent. The button shows
   * an emerald count badge when filters are active.
   */
  onFilterCandidates?: () => void;
  filterActiveCount?: number;
  filterOpen?: boolean;
  /**
   * Segmented view mode toggle (list vs grid). Parent persists the
   * choice. When `onChangeViewMode` is omitted the control is hidden,
   * so non-canvas callers (if any) don't get a stray toggle.
   */
  viewMode?: 'grid' | 'list';
  onChangeViewMode?: (next: 'grid' | 'list') => void;
  /**
   * Group-by selector (21-Apr audit gap). When `onChangeGroupBy` is
   * omitted the selector is hidden — the toolbar caller decides when
   * grouping is meaningful (e.g. Sourcing tab in grid view).
   */
  groupBy?: string;
  onChangeGroupBy?: (next: string) => void;
  /**
   * Unique source-channel values from the current result set. When provided,
   * each channel name is listed as an individual option in the Group selector
   * so recruiters can instantly scope to e.g. "Naukri" or "LinkedIn".
   */
  channelOptions?: readonly { id: string; label: string; count?: number }[];
  /** Show the keyboard-shortcuts helper popover. */
  onToggleKbd?: () => void;
  /** Whether the kbd popover is currently open (controls aria-expanded). */
  kbdOpen?: boolean;
  /**
   * Optional React slot rendered after the tab pills and before the
   * result-action chips. Used by the Sourcing / Smart Match canvas to
   * hoist the "Shortlist All (N)" + "Select a collection" pair up from
   * the old middle-column strip into this single toolbar (post
   * user-feedback, 21-Apr). Callers own the local state (picker open,
   * bulk busy, selected collection) — this is a pure layout slot.
   */
  bulkActionsLeading?: React.ReactNode;
  /**
   * Chat panel toggle (post user-feedback, 21-Apr). When provided, a
   * "Chat" button renders in the toolbar; pressing it flips the agent
   * column open/closed, giving users full-width cards when they want
   * to scan, and a visible chat when they want to converse. The
   * toggle state (`agentOpen`) is owned by the parent so the button
   * mirrors the actual column visibility.
   */
  agentOpen?: boolean;
  onToggleAgent?: () => void;
}

export function NextStepBridge({
  threadId,
  visibleMatches,
  tickedKeys,
  collection,
  onDraftOutreach,
  onCompareStub,
  pushToast,
  onNewChat,
  showActionChips = true,
  tabs,
  activeTabId,
  onTabPick,
  onFilterCandidates,
  filterActiveCount = 0,
  filterOpen = false,
  viewMode = 'grid',
  onChangeViewMode,
  groupBy = 'none',
  onChangeGroupBy,
  channelOptions = [],
  onToggleKbd,
  kbdOpen,
  bulkActionsLeading,
  agentOpen,
  onToggleAgent,
}: NextStepBridgeProps) {
  const navigate = useNavigate();
  const [reportsOpen, setReportsOpen] = useState(false);
  const reportsRef = useRef<HTMLDivElement | null>(null);

  // Close Reports menu on outside click / Escape.
  useEffect(() => {
    if (!reportsOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!reportsRef.current) return;
      if (!reportsRef.current.contains(e.target as Node)) setReportsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setReportsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [reportsOpen]);

  const tickedCount = tickedKeys.size;
  const canCompare = tickedCount >= 2;

  const handleCreateSequence = useCallback(() => {
    // §29.D chip 3 — route to /sequences/new with scope + threadId so the
    // editor can pre-bind once the route lands.
    const scope =
      tickedCount > 0
        ? 'ticked'
        : collection
          ? 'shortlisted'
          : 'top';

    // C-21 · Enrolment handoff. Stash the candidate ids that should be enrolled
    // into whatever sequence the "Create sequence" flow eventually produces.
    // SequencePage consumes the stash after the new sequence loads and calls
    // sequenceRecipientsService.enroll(). Scope gates which candidates we stash.
    const selectedCandidateIds = collectCandidateIds(visibleMatches, tickedKeys, scope);
    stashPendingRecipients(threadId, selectedCandidateIds);

    navigate(
      `/sequences/new?prefill=smartMatch&threadId=${encodeURIComponent(threadId)}&scope=${scope}`,
    );
  }, [navigate, threadId, tickedCount, collection, visibleMatches, tickedKeys]);

  /*
   * 21-Apr follow-up — Reports dropdown restructure (image 3 parity).
   * The menu now exposes *two* scopes:
   *   - selection → rows the user has ticked (`tickedKeys`).
   *     If nothing is ticked, the selection rows fall back to the
   *     visible set so the menu never feels broken; we surface the
   *     scope name in the toast so the user knows what was exported.
   *   - all       → every row currently visible (post-filter / post-
   *     tab). This is the previous "Export visible" behaviour.
   *
   * Keeping row shape identical across scopes means the downstream
   * CSV / Excel / PDF serialisers don't care which list they were
   * handed.
   */
  // 21-Apr N×M reporting — group-aware row shape. When `groupBy` is
  // set the exported rows carry a `group` column (client name or JD
  // title) and are pre-sorted inside their bucket so spreadsheet
  // sub-totalling / pivoting Just Works. When `groupBy === 'none'` the
  // column is still emitted (empty) so all three formats produce
  // identical headers and downstream tooling doesn't break.
  type ReportRow = {
    group: string;
    rank: number;
    candidate_name: string;
    candidate_email: string;
    client_name: string;
    job_title: string;
    score: number;
    band: string;
    matched_skills: string;
    missing_skills: string;
    recommendation: string;
  };

  const toReportRow = useCallback(
    (m: MatchResult, rank: number): ReportRow => {
      const raw = Number.isFinite(m.composite_score)
        ? Number(m.composite_score)
        : 0;
      // Accept both 0-1 (legacy persisted rows) and 0-100 (prompt
      // search output) so the column is always a true percentage.
      const score = Math.round(
        Math.max(0, Math.min(100, raw > 1 ? raw : raw * 100)),
      );
      const band =
        score >= 75
          ? 'Strong'
          : score >= 55
            ? 'Good'
            : score >= 35
              ? 'Partial'
              : 'Not recommended';
      const clientName = clientNameFromMatch(m);
      const jobTitle = jobTitleFromMatch(m);
      const groupKey =
        groupBy === 'client'
          ? clientName
          : groupBy === 'job'
            ? jobTitle
            : groupBy === 'channel' || groupBy.startsWith('channel:')
              ? (m.candidate?.source_channel ?? 'No channel')
              : '';
      return {
        group: groupKey,
        rank,
        candidate_name: m.candidate?.full_name ?? '',
        candidate_email: m.candidate?.email ?? '',
        client_name: clientName,
        job_title: jobTitle,
        score,
        band,
        matched_skills: (m.matched_skills ?? []).join('|'),
        missing_skills: (m.missing_skills ?? []).join('|'),
        recommendation: m.recommendation ?? '',
      };
    },
    [groupBy],
  );

  // Stable grouped-then-ranked projection. Rank restarts per group
  // when `groupBy !== 'none'` so spreadsheets read "Top 5 for Client
  // X" naturally; otherwise rank runs across the whole visible list.
  const rankedRowsFor = useCallback(
    (matches: MatchResult[]): ReportRow[] => {
      if (groupBy === 'none') {
        return matches.map((m, i) => toReportRow(m, i + 1));
      }
      const buckets = new Map<string, MatchResult[]>();
      for (const m of matches) {
        const key =
          groupBy === 'client'
            ? clientNameFromMatch(m)
            : groupBy === 'channel' || groupBy.startsWith('channel:')
              ? (m.candidate?.source_channel ?? 'No channel')
              : jobTitleFromMatch(m);
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key)!.push(m);
      }
      const out: ReportRow[] = [];
      // Sort groups alphabetically so the export order is deterministic
      // and matches what the user sees on the canvas.
      const orderedKeys = Array.from(buckets.keys()).sort();
      for (const key of orderedKeys) {
        const items = (buckets.get(key) ?? [])
          .slice()
          .sort((a, b) => b.composite_score - a.composite_score);
        items.forEach((m, i) => out.push(toReportRow(m, i + 1)));
      }
      return out;
    },
    [groupBy, toReportRow],
  );

  const rowsForScope = useCallback(
    (scope: 'selection' | 'all'): { rows: ReportRow[]; scopeLabel: string } => {
      if (scope === 'selection') {
        const ticked = visibleMatches.filter((m) =>
          tickedKeys.has(matchKey(m)),
        );
        if (ticked.length > 0) {
          return { rows: rankedRowsFor(ticked), scopeLabel: 'selection' };
        }
        return {
          rows: rankedRowsFor(visibleMatches),
          scopeLabel: 'visible (no selection)',
        };
      }
      return { rows: rankedRowsFor(visibleMatches), scopeLabel: 'list' };
    },
    [visibleMatches, tickedKeys, rankedRowsFor],
  );

  const handleExportCSV = useCallback(
    (scope: 'selection' | 'all') => {
      const { rows, scopeLabel } = rowsForScope(scope);
      if (rows.length === 0) {
        pushToast?.('No candidates to export.', 'err');
        return;
      }
      const csv = serialiseCsv(rows);
      downloadBlob(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        `smart-match-${threadId}-${scope}-${Date.now()}.csv`,
      );
      pushToast?.(
        `Exported ${rows.length} candidates (CSV · ${scopeLabel}).`,
        'ok',
      );
    },
    [rowsForScope, threadId, pushToast],
  );

  // Excel export — we emit an HTML table with the .xls extension and an
  // application/vnd.ms-excel MIME type. Excel opens this natively and
  // preserves formatting without a heavy SheetJS dependency. Users that
  // want a strict .xlsx can open + re-save once.
  const handleExportExcel = useCallback(
    (scope: 'selection' | 'all') => {
      const { rows, scopeLabel } = rowsForScope(scope);
      if (rows.length === 0) {
        pushToast?.('No candidates to export.', 'err');
        return;
      }
      const headers = Object.keys(rows[0]!);
      const esc = (v: string | number) =>
        String(v ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      const thead = `<tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`;
      const tbody = rows
        .map(
          (row) =>
            `<tr>${headers.map((h) => `<td>${esc(row[h as keyof typeof row])}</td>`).join('')}</tr>`,
        )
        .join('');
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body><table border="1">${thead}${tbody}</table></body></html>`;
      downloadBlob(
        new Blob([html], { type: 'application/vnd.ms-excel' }),
        `smart-match-${threadId}-${scope}-${Date.now()}.xls`,
      );
      pushToast?.(
        `Exported ${rows.length} candidates (Excel · ${scopeLabel}).`,
        'ok',
      );
    },
    [rowsForScope, threadId, pushToast],
  );

  // PDF export — open a print-friendly window and invoke the browser's
  // print dialog. Users get "Save as PDF" on every modern browser.
  // Simpler and more reliable than bundling jsPDF for this small table.
  const handleExportPDF = useCallback(
    (scope: 'selection' | 'all') => {
      const { rows, scopeLabel } = rowsForScope(scope);
      if (rows.length === 0) {
        pushToast?.('No candidates to export.', 'err');
        return;
      }
      const headers = Object.keys(rows[0]!);
      const esc = (v: string | number) =>
        String(v ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      const rowsHtml = rows
        .map(
          (row) =>
            `<tr>${headers.map((h) => `<td>${esc(row[h as keyof typeof row])}</td>`).join('')}</tr>`,
        )
        .join('');
      const win = window.open(
        '',
        '_blank',
        'noopener,noreferrer,width=1000,height=800',
      );
      if (!win) {
        pushToast?.('Popup blocked — allow popups to print PDFs.', 'err');
        return;
      }
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Smart Match · ${threadId}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; margin: 24px; }
          h1 { font-size: 16px; margin: 0 0 16px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #E5E7EB; padding: 6px 8px; text-align: left; vertical-align: top; }
          th { background: #F3F4F6; font-weight: 600; }
          @media print { @page { size: A4 landscape; margin: 12mm; } }
        </style></head><body>
        <h1>Smart Match export — ${rows.length} candidates (${scopeLabel})</h1>
        <table><thead><tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table>
        <script>window.onload=()=>{window.print();}</script>
      </body></html>`);
      win.document.close();
      pushToast?.(
        `Printing ${rows.length} candidates (${scopeLabel}) — choose "Save as PDF".`,
        'ok',
      );
    },
    [rowsForScope, threadId, pushToast],
  );

  const handleExplainResults = useCallback(() => {
    // Post-image-review: Assistant lives in the middle canvas, not a slide-over.
    // Push the prompt into the existing canvas agent thread via custom event.
    window.dispatchEvent(
      new CustomEvent('ggj:agent-thread:external-prompt', {
        detail: {
          scopeId: threadId,
          text: 'Why did these candidates score this way?',
        },
      }),
    );
    pushToast?.('Asking the agent — see the reasoning pane on the left.', 'ok');
  }, [threadId, pushToast]);

  const compareTitle = useMemo(
    () =>
      canCompare
        ? 'Compare the ticked candidates side-by-side'
        : 'Select at least 2 to compare',
    [canCompare],
  );

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b px-4 py-2"
      style={{ borderColor: 'var(--vscode-border-primary)' }}
      data-ggj-next-step-bridge
      role="toolbar"
      aria-label="Next step actions"
    >
      {/* Leading: + New chat.
          Rendered on every canvas state (empty + results) when the parent
          opts in via `onNewChat`, so the user has one consistent place to
          start a fresh thread. Separator sits between New chat and the
          results-only action chips for visual grouping. */}
      {onNewChat ? (
        <>
          <button
            type="button"
            onClick={onNewChat}
            className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-[var(--ggj-accent,#10B981)] px-3 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
            aria-label="Start a new chat"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New chat
          </button>
          {showActionChips ? (
            <span
              className="mx-1 h-5 w-px shrink-0"
              style={{ background: 'var(--vscode-border-primary)' }}
              aria-hidden
            />
          ) : null}
        </>
      ) : null}

      {/* Agent chat panel toggle (post user-feedback, 21-Apr).
          Icon flips between `PanelLeftClose` (panel is visible, click
          to close) and `PanelLeftOpen` (panel is hidden, click to
          reveal) so the affordance reads as "the left panel goes
          away / comes back". Active styling (emerald soft fill) still
          doubles as a state indicator. */}
      {onToggleAgent ? (
        <button
          type="button"
          onClick={onToggleAgent}
          aria-pressed={agentOpen ?? false}
          className={`inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors ${
            agentOpen
              ? 'bg-[var(--ggj-accent-soft,#D1FAE5)]'
              : 'hover:bg-[var(--vscode-bg-hover)]'
          }`}
          style={{
            borderColor: agentOpen
              ? 'var(--ggj-accent,#10B981)'
              : 'var(--vscode-border-primary)',
            color: agentOpen ? '#065F46' : 'var(--vscode-text-secondary)',
          }}
          aria-label={agentOpen ? 'Hide chat panel' : 'Show chat panel'}
          title={agentOpen ? 'Hide chat' : 'Show chat'}
        >
          {agentOpen ? (
            <PanelLeftClose className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <PanelLeftOpen className="h-3.5 w-3.5" aria-hidden />
          )}
          Chat
        </button>
      ) : null}

      {showActionChips ? (
        <>
      {/* Tab pills (Sourcing / Shortlisted / Hidden).
          Folded into this toolbar (post user-feedback, 21-Apr) so the
          canvas has exactly one action row instead of two stacked bars.
          The divider to the right separates the tab group from the
          result-action chips that follow. */}
      {tabs && tabs.length > 0 ? (
        <>
          <div className="flex items-center gap-1" role="tablist" aria-label="Match status">
            {tabs.map((t) => {
              const active = activeTabId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => onTabPick?.(t.id)}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2.5 text-[12px] font-medium transition-colors ${
                    active
                      ? 'bg-[var(--ggj-accent-soft,#D1FAE5)] text-[#065F46]'
                      : 'text-[color:var(--vscode-text-secondary)] hover:bg-[var(--vscode-bg-hover)]'
                  }`}
                >
                  {t.label}
                  <span
                    className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
                    style={{
                      background: active ? 'rgba(6,95,70,0.12)' : 'var(--vscode-bg-hover)',
                      color: active ? '#065F46' : 'var(--vscode-text-tertiary)',
                    }}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
          <span
            className="mx-1 h-5 w-px shrink-0"
            style={{ background: 'var(--vscode-border-primary)' }}
            aria-hidden
          />
        </>
      ) : null}

      {/* Bulk actions slot (Shortlist All + Select a collection) —
          positioned between the tab pills and the result-action chips
          so the "saving scope" controls sit next to the tabs they
          modify. */}
      {bulkActionsLeading ? (
        <>
          {bulkActionsLeading}
          <span
            className="mx-1 h-5 w-px shrink-0"
            style={{ background: 'var(--vscode-border-primary)' }}
            aria-hidden
          />
        </>
      ) : null}

      {/* 1. Draft outreach */}
      <button
        type="button"
        onClick={onDraftOutreach}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--ggj-accent-soft,#D1FAE5)]"
        style={{ borderColor: 'var(--ggj-accent,#10B981)', color: '#065F46' }}
      >
        <Mail className="h-3.5 w-3.5" aria-hidden />
        Draft outreach
      </button>

      {/* 2. Create sequence */}
      <button
        type="button"
        onClick={handleCreateSequence}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <GitBranch className="h-3.5 w-3.5" aria-hidden />
        Create sequence
      </button>

      {/* 3. Compare */}
      <button
        type="button"
        onClick={() => {
          if (!canCompare) {
            pushToast?.('Select at least 2 to compare', 'err');
            return;
          }
          onCompareStub();
        }}
        disabled={!canCompare}
        title={compareTitle}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <SplitSquareHorizontal className="h-3.5 w-3.5" aria-hidden />
        Compare
      </button>

      {/* 4. Reports dropdown — replaces the old single Export CSV
             button so users can pick CSV / Excel / PDF from one chip.
             Popover closes on outside click or Escape. */}
      <div className="relative" ref={reportsRef}>
        <button
          type="button"
          onClick={() => setReportsOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={reportsOpen}
          className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
          }}
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          Reports
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
        {reportsOpen ? (
          <div
            role="menu"
            aria-label="Reports"
            className="absolute left-0 top-full z-30 mt-1 w-[240px] rounded-[10px] border py-1 shadow-lg"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-elevated)',
            }}
          >
            {/* Object History — opens the session's thread history so
                the user can trace past runs. Mirrors image 3 layout. */}
            <ReportMenuItem
              icon={
                <History
                  className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400"
                  aria-hidden
                />
              }
              label="Object History"
              onClick={() => {
                setReportsOpen(false);
                try {
                  window.open('/smart-match', '_blank', 'noopener,noreferrer');
                } catch {
                  /* noop */
                }
              }}
            />

            <MenuDivider />
            <MenuHeader label={`Export (selection${tickedCount > 0 ? ` · ${tickedCount}` : ''})`} />
            <ReportMenuItem
              icon={
                <FileSpreadsheet
                  className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                  aria-hidden
                />
              }
              label="Excel · xlsx"
              onClick={() => {
                setReportsOpen(false);
                handleExportExcel('selection');
              }}
            />
            <ReportMenuItem
              icon={
                <Table2
                  className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400"
                  aria-hidden
                />
              }
              label="CSV"
              onClick={() => {
                setReportsOpen(false);
                handleExportCSV('selection');
              }}
            />

            <MenuDivider />
            <MenuHeader label={`Export all (list · ${visibleMatches.length})`} />
            <ReportMenuItem
              icon={
                <FileSpreadsheet
                  className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400"
                  aria-hidden
                />
              }
              label="Excel — all"
              onClick={() => {
                setReportsOpen(false);
                handleExportExcel('all');
              }}
            />
            <ReportMenuItem
              icon={
                <Table2
                  className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400"
                  aria-hidden
                />
              }
              label="CSV — all"
              onClick={() => {
                setReportsOpen(false);
                handleExportCSV('all');
              }}
            />

            <MenuDivider />
            <MenuHeader label="Print / PDF" />
            <ReportMenuItem
              icon={
                <FileDown
                  className="h-3.5 w-3.5 text-red-600 dark:text-red-400"
                  aria-hidden
                />
              }
              label="PDF — selection"
              onClick={() => {
                setReportsOpen(false);
                handleExportPDF('selection');
              }}
            />
            <ReportMenuItem
              icon={
                <FileDown
                  className="h-3.5 w-3.5 text-red-600 dark:text-red-400"
                  aria-hidden
                />
              }
              label="PDF — all"
              onClick={() => {
                setReportsOpen(false);
                handleExportPDF('all');
              }}
            />
          </div>
        ) : null}
      </div>

      {/* Right-aligned utilities: View toggle / Filter / Keyboard / Explain.
          `ml-auto` on the first of the group pushes the rest to the right
          edge of the toolbar, matching the previous sticky-strip layout
          the user already knows. */}
      {onChangeViewMode ? (
        <div
          className="ml-auto inline-flex h-8 items-center rounded-[10px] border p-0.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
          role="group"
          aria-label="Card view mode"
        >
          <button
            type="button"
            onClick={() => onChangeViewMode('grid')}
            aria-pressed={viewMode === 'grid'}
            title="Grid view"
            className={`inline-flex h-7 w-7 items-center justify-center rounded-[8px] transition-colors ${
              viewMode === 'grid'
                ? 'bg-[var(--ggj-accent-soft,#D1FAE5)] text-[#065F46]'
                : 'text-[color:var(--vscode-text-secondary)] hover:bg-[var(--vscode-bg-hover)]'
            }`}
          >
            <Grid3x3 className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onChangeViewMode('list')}
            aria-pressed={viewMode === 'list'}
            title="List view"
            className={`inline-flex h-7 w-7 items-center justify-center rounded-[8px] transition-colors ${
              viewMode === 'list'
                ? 'bg-[var(--ggj-accent-soft,#D1FAE5)] text-[#065F46]'
                : 'text-[color:var(--vscode-text-secondary)] hover:bg-[var(--vscode-bg-hover)]'
            }`}
          >
            <List className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}

      {/*
       * 21-Apr audit close-out — Group-by selector. Sits next to the
       * view toggle so the two layout controls travel together. Hidden
       * for list view (the selector would be visible but do nothing —
       * confusing) and when `onChangeGroupBy` is not provided.
       */}
      {onChangeGroupBy && viewMode === 'grid' ? (
        <label
          className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-2 text-[11px]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
            background: 'var(--vscode-bg-elevated)',
          }}
          title="Group sourcing results by client or job description"
        >
          <span className="font-medium">Group</span>
          <select
            value={groupBy}
            onChange={(e) => onChangeGroupBy(e.target.value)}
            className="h-6 rounded-[6px] border-0 bg-transparent text-[11px] font-medium outline-none"
            style={{ color: 'var(--vscode-text-primary)' }}
            aria-label="Group sourcing results"
          >
            <option value="none">None</option>
            <option value="client">Client</option>
            <option value="job">JD</option>
            <option value="channel">Channel</option>
            {channelOptions.length > 0 ? (
              <>
                <option disabled>──────────</option>
                {channelOptions.map((ch) => (
                  <option key={ch.id} value={`channel:${ch.id}`}>
                    {ch.label}{ch.count != null ? ` (${ch.count})` : ''}
                  </option>
                ))}
              </>
            ) : null}
          </select>
        </label>
      ) : null}

      {onFilterCandidates ? (
        <button
          type="button"
          onClick={onFilterCandidates}
          aria-expanded={filterOpen}
          aria-haspopup="dialog"
          className={`${onChangeViewMode ? '' : 'ml-auto'} inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-2.5 text-[12px] font-medium transition-colors ${
            filterActiveCount > 0 || filterOpen
              ? 'bg-[var(--ggj-accent-soft,#D1FAE5)]'
              : 'hover:bg-[var(--vscode-bg-hover)]'
          }`}
          style={{
            borderColor:
              filterActiveCount > 0 || filterOpen
                ? 'var(--ggj-accent,#10B981)'
                : 'var(--vscode-border-primary)',
            color:
              filterActiveCount > 0 || filterOpen
                ? '#065F46'
                : 'var(--vscode-text-secondary)',
          }}
        >
          <Filter className="h-3.5 w-3.5" aria-hidden />
          Filter
          {filterActiveCount > 0 ? (
            <span
              className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums"
              style={{ background: 'rgba(6,95,70,0.12)', color: '#065F46' }}
            >
              {filterActiveCount}
            </span>
          ) : null}
        </button>
      ) : null}

      {onToggleKbd ? (
        <button
          type="button"
          onClick={onToggleKbd}
          className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] border transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
          }}
          aria-expanded={kbdOpen}
          aria-label="Keyboard shortcuts"
          title="Keyboard shortcuts"
        >
          <Keyboard className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}

      {/* 5. Explain these results. When none of the right-aligned
             utilities are wired (`ml-auto` owner neither present),
             this button carries the push itself so the chip still
             floats to the right edge. */}
      <button
        type="button"
        onClick={handleExplainResults}
        className={`inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)] ${
          onChangeViewMode || onFilterCandidates ? '' : 'ml-auto'
        }`}
        data-ggj-explain-results
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        Explain these results
      </button>
        </>
      ) : null}
    </div>
  );
}

// ── Candidate id extraction for sequence enrolment handoff (C-21) ──────────

function matchKey(m: MatchResult): string {
  return `${m.candidate_id}-${m.job_id}`;
}

/**
 * Extract the candidate ids to enrol based on the current scope. We prefer
 * explicit ticks; if nothing is ticked we fall back to the visible set so
 * the handoff still works when the user clicks Create sequence without
 * selecting rows first (mirrors the existing `scope` semantics).
 */
function collectCandidateIds(
  visibleMatches: MatchResult[],
  tickedKeys: Set<string>,
  scope: 'ticked' | 'shortlisted' | 'top',
): string[] {
  const source =
    scope === 'ticked' && tickedKeys.size > 0
      ? visibleMatches.filter((m) => tickedKeys.has(matchKey(m)))
      : visibleMatches;

  const ids: string[] = [];
  const seen = new Set<string>();
  for (const m of source) {
    const cid = m.candidate_id;
    if (cid && !seen.has(cid)) {
      seen.add(cid);
      ids.push(cid);
    }
  }
  return ids;
}

// ── Reports dropdown menu item ─────────────────────────────────────────────

function ReportMenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
      style={{ color: 'var(--vscode-text-primary)' }}
    >
      <span
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border"
        style={{
          borderColor:
            'color-mix(in srgb, var(--vscode-border-primary) 45%, transparent)',
          background:
            'color-mix(in srgb, var(--vscode-bg-secondary) 88%, transparent)',
        }}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 whitespace-nowrap text-left leading-tight">
        {label}
      </span>
    </button>
  );
}

function MenuDivider() {
  return (
    <div
      className="my-1 border-t"
      style={{ borderColor: 'var(--vscode-border-primary)' }}
      aria-hidden
    />
  );
}

function MenuHeader({ label }: { label: string }) {
  return (
    <div
      className="px-3 pb-0.5 pt-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ color: 'var(--vscode-text-tertiary)' }}
    >
      {label}
    </div>
  );
}

// ── Client-side file download helper (no deps) ─────────────────────────────

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Minimal CSV serialiser (no papaparse dep required) ──────────────────────

function serialiseCsv(rows: Array<Record<string, string | number>>): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]!);
  const esc = (v: string | number): string => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => esc(row[h] ?? '')).join(','));
  }
  return lines.join('\n');
}
